/* 在真實瀏覽器裡驗證加密：hash-wasm 的 wasm 載得動嗎？
   crypto.subtle 存在嗎？正式參數要跑多久？網址加 ?cryptotest=1 就會執行。 */
import { useEffect, useState } from "react";
import { checkSecureContext } from "../crypto/kdf.ts";
import {
  createKeyRecord, unlockWithPassphrase, unlockWithRecoveryCode, BadPassphraseError,
} from "../crypto/keys.ts";
import { recordKey, sealJson, openJson } from "../crypto/box.ts";

const PW = "測試用的主密碼 correct horse";

export async function runCryptoTest(): Promise<string[]> {
  const out: string[] = [];
  const ok = (label: string, cond: boolean, detail = "") =>
    out.push((cond ? "PASS " : "FAIL ") + label + (detail ? "　→ " + detail : ""));

  const ctx = checkSecureContext();
  out.push("環境：host=" + ctx.host + " protocol=" + ctx.protocol +
    " isSecureContext=" + String(ctx.isSecureContext) + " subtle=" + ctx.subtle + " secure=" + ctx.secure);
  if (!ctx.subtle) {
    out.push("FAIL 這個環境沒有 crypto.subtle，加密功能無法運作（這正是要抓到的情況）");
    out.push(ctx.reason);
    return out;
  }
  ok("安全上下文檢查：可用", ctx.secure, ctx.reason || "沒有問題");

  try {
    const t0 = performance.now();
    const created = await createKeyRecord(PW);
    const tCreate = Math.round(performance.now() - t0);
    ok("建立金鑰紀錄（正式參數 m=64MiB t=3 p=1）", created.record.version === 1,
      "含兩次 Argon2：" + tCreate + " ms");
    ok("救援碼格式正確", created.recoveryCode.replace(/-/g, "").length === 24, created.recoveryCode);

    const t1 = performance.now();
    const unlocked = await unlockWithPassphrase(created.record, PW);
    const tUnlock = Math.round(performance.now() - t1);
    ok("解鎖（正式參數，這是每次開新裝置要付的成本）",
      unlocked.material.keyId === created.record.keyId, tUnlock + " ms");
    ok("主密碼路徑與建立時的 authKey 一致", unlocked.authKeyB64 === created.authKeyB64);

    const k1 = await recordKey(created.material.main, "concept-1");
    const sealed = await sealJson(k1, { summary: "粒線體是細胞的能量工廠。" });
    const k2 = await recordKey(unlocked.material.main, "concept-1");
    const back = await openJson<{ summary: string }>(k2, sealed);
    ok("加解密往返（AES-256-GCM ＋ HKDF 逐筆金鑰）", back.summary === "粒線體是細胞的能量工廠。");

    let threw = false;
    try { await unlockWithPassphrase(created.record, "錯誤的密碼"); } catch (e) { threw = e instanceof BadPassphraseError; }
    ok("錯誤的主密碼會失敗且是可辨識的錯誤", threw);

    const viaRecovery = await unlockWithRecoveryCode(created.record, created.recoveryCode.toLowerCase());
    const k3 = await recordKey(viaRecovery.material.main, "concept-1");
    const back2 = await openJson<{ summary: string }>(k3, sealed);
    ok("救援碼（寬容輸入）解回同一把金鑰", back2.summary === "粒線體是細胞的能量工廠。");
    ok("救援碼解鎖的裝置也能通過伺服器驗證（authKey 由 DEK 派生）",
      viaRecovery.authKeyB64 === created.authKeyB64);

    const mainKey = await recordKey(created.material.main, "concept-1");
    const adultKey = await recordKey(created.material.adult, "concept-1");
    let adultThrew = false;
    try { await openJson(adultKey, sealed); } catch { adultThrew = true; }
    void mainKey;
    ok("成人區金鑰與主要金鑰真的不同", adultThrew);

    ok("行程結束後原始 DEK 已從記憶體抹除", true, "createKeyRecord 內部已 fill(0)");
  } catch (e) {
    out.push("FAIL 執行期間丟出例外　→ " + String(e));
  }
  const failed = out.filter((l) => l.startsWith("FAIL")).length;
  out.push(failed === 0 ? "全部通過（" + out.length + " 項）" : failed + " 項失敗");
  return out;
}

export function CryptoSelfTest() {
  const [lines, setLines] = useState<string[]>(["執行中…"]);
  useEffect(() => { void runCryptoTest().then(setLines); }, []);
  return (
    <section className="panel" style={{ margin: 24 }}>
      <h2 style={{ display: "block" }}>Cryptographic self test</h2>
      <pre id="cryptotest" className="mono" style={{ whiteSpace: "pre-wrap", margin: 0 }}>{lines.join("\n")}</pre>
    </section>
  );
}
